/**
 * @fileoverview DatePickerInput Component
 *
 * A reusable date picker component using shadcn Calendar and Popover components.
 * Supports direct text input (dd/mm/yyyy) and year/month dropdown navigation.
 * With showTime={true}: combined input (dd/mm/yyyy HH:mm), calendar button,
 * and dedicated hour/minute steppers inside the popover for user-friendly time picking.
 */

import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { nl } from "date-fns/locale";
import { Calendar as CalendarIcon, Clock, CaretUp, CaretDown } from "@phosphor-icons/react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import * as Sentry from "@sentry/react";

interface DatePickerInputProps {
  value?: string | Date;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  showTime?: boolean;
}

const DATE_FORMAT = "dd/MM/yyyy";
const DATETIME_FORMAT = "dd/MM/yyyy HH:mm";

const clampHour = (n: number) => Math.max(0, Math.min(23, n));
const clampMinute = (n: number) => Math.max(0, Math.min(59, n));
const pad = (n: number) => n.toString().padStart(2, "0");

export const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  className,
  id,
  showTime = false,
}) => {
  const resolvedPlaceholder = placeholder ?? (showTime ? "dd/mm/yyyy HH:mm" : "dd/mm/yyyy");

  const parseInitialDate = (v: string | Date | undefined): Date | undefined => {
    if (!v) return undefined;
    try {
      const d = new Date(v);
      return isValid(d) ? d : undefined;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "DatePickerInput", action: "parseInitialDate" },
        extra: { value: v },
      });
      return undefined;
    }
  };

  const formatText = (d: Date): string =>
    format(d, showTime ? DATETIME_FORMAT : DATE_FORMAT);

  const [date, setDate] = React.useState<Date | undefined>(parseInitialDate(value));
  const [inputText, setInputText] = React.useState<string>(
    parseInitialDate(value) ? formatText(parseInitialDate(value)!) : "",
  );

  React.useEffect(() => {
    const parsed = parseInitialDate(value);
    if (parsed) {
      setDate(parsed);
      setInputText(formatText(parsed));
    } else {
      setDate(undefined);
      setInputText("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, showTime]);

  const emitValue = (d: Date) => {
    try {
      onChange(showTime ? d.toISOString() : format(d, "yyyy-MM-dd"));
    } catch (error) {
      Sentry.captureException(error, {
        tags: { component: "DatePickerInput", action: "emitValue" },
        extra: { date: d?.toString() },
      });
      throw error;
    }
  };

  const handleSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      setDate(undefined);
      setInputText("");
      onChange("");
      return;
    }
    const result = new Date(selectedDate);
    if (showTime && date) {
      result.setHours(date.getHours(), date.getMinutes(), 0, 0);
    } else if (showTime) {
      result.setHours(9, 0, 0, 0);
    } else {
      result.setHours(0, 0, 0, 0);
    }
    setDate(result);
    setInputText(formatText(result));
    emitValue(result);
  };

  const handleTextInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setInputText(raw);

    const targetFormat = showTime ? DATETIME_FORMAT : DATE_FORMAT;
    const targetLength = showTime ? 16 : 10;

    if (raw.length === targetLength) {
      try {
        const parsed = parse(raw, targetFormat, new Date());
        if (isValid(parsed)) {
          setDate(parsed);
          emitValue(parsed);
        }
      } catch (error) {
        Sentry.captureException(error, {
          tags: { component: "DatePickerInput", action: "handleTextInput" },
          extra: { raw, targetFormat },
        });
      }
    }
  };

  const updateTime = (nextHours: number, nextMinutes: number) => {
    if (!date) return;
    const result = new Date(date);
    result.setHours(clampHour(nextHours), clampMinute(nextMinutes), 0, 0);
    setDate(result);
    setInputText(formatText(result));
    emitValue(result);
  };

  const hasDate = Boolean(date);
  const hour = date ? date.getHours() : 0;
  const minute = date ? date.getMinutes() : 0;

  const handleHourInput = (raw: string) => {
    if (!hasDate || raw === "") return;
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    updateTime(clampHour(n), minute);
  };

  const handleMinuteInput = (raw: string) => {
    if (!hasDate || raw === "") return;
    const n = Number(raw);
    if (Number.isNaN(n)) return;
    updateTime(hour, clampMinute(n));
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className={cn("flex flex-col gap-1.5", className)}>
      <div className="flex gap-2">
        <input
          id={id}
          type="text"
          value={inputText}
          onChange={handleTextInput}
          placeholder={resolvedPlaceholder}
          disabled={disabled}
          maxLength={showTime ? 16 : 10}
          className={cn(
            "border-input bg-background flex h-9 w-full rounded-md border px-3 py-1 text-sm shadow-sm transition-colors",
            "placeholder:text-muted-foreground focus-visible:ring-ring focus-visible:ring-1 focus-visible:outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
          )}
        />
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              disabled={disabled}
              className="shrink-0 px-3"
            >
              <CalendarIcon size={16} />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="single"
              selected={date}
              onSelect={handleSelect}
              initialFocus
              locale={nl}
              captionLayout="dropdown"
              fromYear={1900}
              toYear={currentYear + 20}
            />
            {showTime && (
              <div className="border-t border-neutral-primary/15 p-3">
                {hasDate ? (
                  <>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2 text-sm text-neutral-secondary-mid">
                        <Clock size={16} weight="duotone" />
                        <span>Tijd</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <TimeStepper
                          value={hour}
                          onChange={(n) => updateTime(n, minute)}
                          onTextChange={handleHourInput}
                          min={0}
                          max={23}
                          ariaLabel="Uur"
                        />
                        <span className="text-sm font-medium text-neutral-primary">:</span>
                        <TimeStepper
                          value={minute}
                          onChange={(n) => updateTime(hour, n)}
                          onTextChange={handleMinuteInput}
                          min={0}
                          max={59}
                          step={5}
                          ariaLabel="Minuten"
                        />
                      </div>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      {[
                        ["09:00", 9, 0],
                        ["10:00", 10, 0],
                        ["13:00", 13, 0],
                        ["14:00", 14, 0],
                        ["16:00", 16, 0],
                        ["17:30", 17, 30],
                      ].map(([label, h, m]) => (
                        <button
                          key={label as string}
                          type="button"
                          onClick={() => updateTime(h as number, m as number)}
                          className={cn(
                            "h-7 rounded-md border px-2 text-xs transition-colors",
                            hour === h && minute === m
                              ? "border-moss-primary bg-moss-primary text-white"
                              : "border-neutral-primary/20 text-neutral-primary hover:border-moss-primary hover:text-moss-primary",
                          )}
                        >
                          {label as string}
                        </button>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-neutral-secondary-mid">
                    <Clock size={16} weight="duotone" />
                    <span>Kies eerst een datum om een tijd in te stellen</span>
                  </div>
                )}
              </div>
            )}
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
};

interface TimeStepperProps {
  value: number;
  onChange: (next: number) => void;
  onTextChange: (raw: string) => void;
  min: number;
  max: number;
  step?: number;
  ariaLabel: string;
}

const TimeStepper: React.FC<TimeStepperProps> = ({
  value,
  onChange,
  onTextChange,
  min,
  max,
  step = 1,
  ariaLabel,
}) => {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  const increment = () => onChange(clamp(value + step));
  const decrement = () => onChange(clamp(value - step));

  return (
    <div className="flex items-center">
      <button
        type="button"
        onClick={decrement}
        aria-label={`${ariaLabel} verlagen`}
        className="flex h-7 w-6 items-center justify-center rounded-l-md border border-r-0 border-neutral-primary/20 text-neutral-primary hover:bg-neutral-100 hover:text-moss-primary"
      >
        <CaretDown size={12} weight="bold" />
      </button>
      <input
        type="text"
        inputMode="numeric"
        value={pad(value)}
        onChange={(e) => onTextChange(e.target.value.replace(/[^0-9]/g, "").slice(0, 2))}
        aria-label={ariaLabel}
        className="h-7 w-10 border border-neutral-primary/20 bg-white text-center text-sm tabular-nums text-neutral-primary focus:border-moss-primary focus:outline-none"
      />
      <button
        type="button"
        onClick={increment}
        aria-label={`${ariaLabel} verhogen`}
        className="flex h-7 w-6 items-center justify-center rounded-r-md border border-l-0 border-neutral-primary/20 text-neutral-primary hover:bg-neutral-100 hover:text-moss-primary"
      >
        <CaretUp size={12} weight="bold" />
      </button>
    </div>
  );
};
